import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/ManagementControlDocumentation.scss';
import { getChecklist } from '../config/documentChecklists';

const documents = getChecklist('management_control');

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/>
    <line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const ManagementControlDocumentation = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [activeDoc, setActiveDoc] = useState(null);
  const [uploads, setUploads] = useState({});
  const [uploading, setUploading] = useState(false);

  const handleClick = (doc) => {
    setActiveDoc(doc);
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleUpload = async (files) => {
    if (!files.length || !activeDoc) return;

    setUploading(true);
    const uploaded = [];

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', activeDoc);
        formData.append('element', 'management_control');
        await api.post('/core/documents/upload/', formData);
        uploaded.push(file.name);
      } catch (err) {
        console.error('Upload error:', err);
      }
    }

    if (uploaded.length) {
      setUploads((prev) => ({
        ...prev,
        [activeDoc]: [...(prev[activeDoc] || []), ...uploaded],
      }));
    }

    setUploading(false);
  };

  useEffect(() => {
    api.get('/core/documents/?element=management_control')
      .then((res) => {
        const existing = {};
        res.data.forEach((doc) => {
          if (!existing[doc.doc_type]) existing[doc.doc_type] = [];
          existing[doc.doc_type].push(doc.filename);
        });
        setUploads(existing);
      })
      .catch((err) => console.error('Failed to load documents:', err));
  }, []);

  const uploadedCount = documents.filter((doc) => uploads[doc]?.length > 0).length;
  const total = documents.length;
  const percentage = Math.round((uploadedCount / total) * 100);
  const radius = 58;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="od-container">
      <div className="od-header">
        <button className="od-back" onClick={() => navigate('/management/management-control')}>← Back</button>
        <div>
          <h1>Documentation</h1>
          <p>Management Control — Required Documents &amp; Evidence</p>
        </div>
        <div className="od-progress-ring" title={`${uploadedCount} of ${total} documents uploaded`}>
          <svg width="138" height="138" viewBox="0 0 138 138">
            <circle
              className="od-progress-ring__track"
              cx="69" cy="69" r={radius}
              fill="none"
              strokeWidth="15"
            />
            <circle
              className="od-progress-ring__fill"
              cx="69" cy="69" r={radius}
              fill="none"
              strokeWidth="15"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              transform="rotate(-90 69 69)"
            />
          </svg>
          <div className="od-progress-ring__label">
            <span className="od-progress-ring__pct">{percentage}%</span>
            <span className="od-progress-ring__sub">{uploadedCount}/{total}</span>
          </div>
        </div>
      </div>

      <div className="od-section-label">Select a document to upload (multiple files allowed)</div>

      <div className="od-grid">
        {documents.map((doc) => {
          const docFiles = uploads[doc] || [];
          return (
            <button
              key={doc}
              className={`od-card ${docFiles.length ? 'od-card--uploaded' : ''}`}
              onClick={() => handleClick(doc)}
              disabled={uploading}
            >
              <div className="od-card-icon">
                <UploadIcon />
              </div>
              <span className="od-card-label">{doc}</span>
              {docFiles.length > 0 && (
                <div className="od-card-files">
                  {docFiles.map((name, i) => (
                    <span key={i} className="od-card-status">✓ {name}</span>
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.pdf,.docx,.jpg,.png"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleUpload(Array.from(e.target.files))}
      />
    </div>
  );
};

export default ManagementControlDocumentation;
